import * as Styles from '../styles';
import ApplicationCard from '../Cards/ApplicationCard';
import DriveApplicationCard from '../Cards/DriveApplicationCard';
import MobileApplicationCard from '../Cards/MobileApplicationCard';
import { Application } from '../../types';
import { canOpenDrive } from '../../util/DriveUtils';
import { DRIVE_TITLE_ID, UNIFYADMIN_APP_ID, DEVICE } from '../../constants';
import React from 'react';
import { useEffect } from 'react';
import InfoDialog from '../InfoDialog/InfoDialog';
import { usePaneManagement } from '../../contexts/PaneManagementContext';
import { useUnifyAdmin } from '../UnifyAdministration/Hooks/useUnifyAdmin';
import { useTranslation } from 'react-i18next';
import launchExternal from '../../util/launchExternal';
import { useUnifyAdminStore } from '../UnifyAdministration/AdminStore';
import { detectDevice } from '../../util/mobileAppUtils';
import useAudit from '../../hooks/useAudit';
import {  useAllApplicationsStore, useRouteStore, useUserIPRestrictionStore } from '../../Store'
import { setDocumentTitle } from '../../util/TitleManager';
import {IUserIPRestrictionEntity} from '../DealerAdmin/IPRestrictionInterface';
import toTestId from '../../testUtils/toTestId';
import { useUnifyProfileStore } from '../../Store';

const findIpRestricted = (ipRestriction:IUserIPRestrictionEntity[], appId:string) =>{
  const data = ipRestriction?.some(
    (item:IUserIPRestrictionEntity) => item.entityType === 'application' 
    && item.entityId === appId 
    && item.isRestricted
  );
  return data;
}

const getTooltipMsg = (ipRestriction:IUserIPRestrictionEntity[], appId:string) =>{
  const data = ipRestriction?.find(
    (item:IUserIPRestrictionEntity) => item.entityType === 'application' 
    && item.entityId === appId 
    && item.isRestricted
  );
  return data;
}

export default function ApplicationsList({
  applications,
}: {
  applications: Application[];
}) {
  const ipRestriction = useUserIPRestrictionStore((state:any) => state.ipRestriction);
  const { unifyProfile } = useUnifyProfileStore();
  const setRoute =  useRouteStore((state:any) => state.setRoute);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const { openPane, openCdkuPane, openCtaPane } = usePaneManagement();
  const {t} = useTranslation();
  const restrictedTooltipMsg = `${t('applicationList.restrictedTooltipMsg')}`; 

  const { captureEvent} = useAudit();
  const { isLoading: isLoadingAdmin, isUnifyEnterpriseAdmin: isUnifyEnterpriseAdminData } = useUnifyAdmin();
  const setIsUnifyEnterpriseAdmin = useUnifyAdminStore(state => state.setIsUnifyEnterpriseAdmin);
  const isUnifyEnterpriseAdmin = useUnifyAdminStore(state => state.isUnifyEnterpriseAdmin); 
  const device = detectDevice();
  const setDeepLinkProgress = useAllApplicationsStore(state => state.setDeepLinkProgress);

  const isAppDefault = (appId: string): boolean => {
    if (!unifyProfile?.apps || !Array.isArray(unifyProfile.apps)) return false;
    return unifyProfile.apps.some(
      (app: any) => app.appId === appId && app.isDefaultApp === true
    );
  };

  useEffect(() => {
    setIsUnifyEnterpriseAdmin(isUnifyEnterpriseAdminData);
  }, [setIsUnifyEnterpriseAdmin, isUnifyEnterpriseAdminData]);

  const closeDialog = () => {
    setErrorMessage(null);
  };

  const onCardOpen = (id: string, name: string, url: string) => {
    setDocumentTitle(name, url);
    const message = `${t('applicationList.troubleLaunchMsg')} ${name}.`;
    try {
      //Adding to opened apps in local storage for AdoptEase
      const openedApps = JSON.parse(localStorage.getItem('opened-apps') || '[]');
      if (!openedApps.includes(id)) {
        openedApps.push(id);
        localStorage.setItem('opened-apps', JSON.stringify(openedApps));
      }

      captureEvent({action:"Opened",type:"Application", name});
      launchExternal(url) || openPane(id);
    } catch {
      setErrorMessage(message);
    }
  };

  useEffect(() => {
    try {
      if (!applications) return;
      let timer: any;
      /***  DEEP LINK Handling */
      const deepLink = localStorage.getItem('deep-link-url');
      const deepLinkParams = deepLink?.substring(0, deepLink.indexOf('/')).split('_');
      const deepLinkAppId = deepLinkParams && deepLinkParams[0] === 'ap' ? deepLinkParams[1] : '';
      const deepLinkUrl = deepLink?.substring(deepLink.indexOf('/') + 1);
      const app = applications.find((app) => app.id === deepLinkAppId);
      if (deepLinkUrl && deepLinkUrl.includes('GS=true')) {
        openCtaPane(deepLinkUrl.replace('?GS=true', ''));
        setDeepLinkProgress(false);
        const linkFunction = () => {
          const frmWindow: any = document.getElementById("ctapane");
          if (frmWindow) {
            frmWindow.style.visibility = "visible";
            frmWindow.style.setProperty("visibility", "visible", "important");
            frmWindow.classList.remove("hide")
          }
          localStorage.removeItem('deep-link-url');
        }
        timer = setTimeout(linkFunction, 1000);
      }
      else {
        const clickFunction = () => {
          if (deepLink) {
            if (deepLinkAppId) {
              const ele = document.getElementById('wf-card-' + toTestId(app?.name || ''));
              app && ele?.click();
              setDeepLinkProgress(false);
              const linkFunction = () => {
                const frmWindow: any = document.getElementById(deepLinkAppId);
                frmWindow && (frmWindow.src = deepLinkUrl?.replace('?GS=true', ''));
                localStorage.removeItem('deep-link-url');
              }
              timer = setTimeout(linkFunction, deepLinkAppId === 'DRC' ? 8000 : 600);
            }
          }
        }
        const clickTimer = setTimeout(clickFunction, 1600);
        return () => {
          clearTimeout(clickTimer);
          clearTimeout(timer);
        }
      }
      return () => {
        clearTimeout(timer);
      }
    }
    catch (e) {
      console.log(e);
    }
    // eslint-disable-next-line
  }, [applications, setDeepLinkProgress]);

  const  onCdkuOpen = (url: string) => {
    openCdkuPane('CDKU', url);
  };

    return (
        <>
          {errorMessage && (
              <InfoDialog
                  title={t('applicationList.infoTitle')}
                  description={errorMessage}
                  isOpen
                  handleClose={() => closeDialog()}
              />
          )}
        {applications?.length ? (
          <Styles.CardList>
            {applications.map((app) => {
              return app.id === DRIVE_TITLE_ID ? (
                <DriveApplicationCard
                  key={app.id}
                  name={app.name}
                  description={app.description}
                  url={app.url}
                  isFavorite={app.isFavorite}
                  isEnabled={canOpenDrive()}
                  iconColor={app.icon}
                  iconText={app.iconText}
                  id={app.id}
                  onCdkuOpen={onCdkuOpen}
                  cdkuUrl={app.cdkuUrl || ''}
                  isRestricted={findIpRestricted(ipRestriction,app.id)}
                  restrictedTooltipMsg={getTooltipMsg(ipRestriction,app.id)?.message || restrictedTooltipMsg}
                  isDefault={isAppDefault(app.id)}
                />
              ) : app.id === UNIFYADMIN_APP_ID ? (
                !isLoadingAdmin &&
                (isUnifyEnterpriseAdmin) &&
                (
                  <ApplicationCard
                    key={app.id}
                    name={app.name}
                    description={app.description}
                    onCardOpen={() => {
                      setDocumentTitle(app.name, '');
                      setRoute('/admin');
                    }}
                    url="www.placeholder.com"
                    isEnabled={!!isUnifyEnterpriseAdmin}
                    isFavorite={app.isFavorite}
                    iconColor={app.icon}
                    iconText={app.iconText}
                    id={app.id}
                    onCdkuOpen={onCdkuOpen}
                    cdkuUrl={app.cdkuUrl || ''}
                    isRestricted={findIpRestricted(ipRestriction,app.id)}
                    restrictedTooltipMsg={getTooltipMsg(ipRestriction,app.id)?.message || restrictedTooltipMsg}
                    showNewIndicator={app.showNewIndicator as boolean}
                    openInNewTab={app.openInNewTab}
                    isDefault={isAppDefault(app.id)}
                  />
                ))
                :
                // Handling Mobile App on Mobile Device 
                (((device === DEVICE.ANDROID || device === DEVICE.IOS) && app.mobileAppUrl) ?
                  (<MobileApplicationCard
                    key={app.id}
                    name={app.name}
                    description={app.description}
                    url={app.mobileAppUrl}
                    isFavorite={app.isFavorite}
                    isEnabled={false}
                    iconColor={app.icon}
                    iconText={app.iconText}
                    id={app.id}
                    onCdkuOpen={onCdkuOpen}
                    cdkuUrl={app.cdkuUrl || ''}
                    playStoreUrl={app.mobileAppDownloadUrl?.playStoreUrl || ''}
                    appStoreUrl={app.mobileAppDownloadUrl?.appStoreUrl || ''}
                    data-testid="mobile_tile"
                    isRestricted={findIpRestricted(ipRestriction,app.id)}
                    restrictedTooltipMsg={getTooltipMsg(ipRestriction,app.id)?.message || restrictedTooltipMsg}
                  />)
                  : 
                  // Handling Mobile App on Desktop, If Mobile App has a Web version then this block will not execute
                  ((device === DEVICE.DESKTOP && app.mobileAppUrl && app.mobileAppUrl !== "" && (!app.url || app.url === "")) ?
                    (<MobileApplicationCard
                      key={app.id}
                      name={app.name}
                      description={app.description}
                      url={app.mobileAppUrl || ''}
                      isFavorite={app.isFavorite}
                      iconColor={app.icon}
                      iconText={app.iconText}
                      id={app.id}
                      onCdkuOpen={onCdkuOpen}
                      cdkuUrl={app.cdkuUrl || ''}
                      playStoreUrl={app.mobileAppDownloadUrl?.playStoreUrl || ''}
                      appStoreUrl={app.mobileAppDownloadUrl?.appStoreUrl || ''}
                      isEnabled={true}
                      data-testid="mobile_tile_desktop"
                      isRestricted={findIpRestricted(ipRestriction,app.id)}
                      restrictedTooltipMsg={getTooltipMsg(ipRestriction,app.id)?.message || restrictedTooltipMsg}
                    />) :
                    (<ApplicationCard
                      key={app.id}
                      name={app.name}
                      description={app.description}
                      url={app.url}
                      isFavorite={app.isFavorite}
                      isEnabled={app.isEnabled ?? true}
                      iconColor={app.icon}
                      onCardOpen={() => onCardOpen(app.id, app.name, app.url)}
                      iconText={app.iconText}
                      id={app.id}
                      onCdkuOpen={onCdkuOpen}
                      cdkuUrl={app.cdkuUrl || ''}
                      isRestricted={findIpRestricted(ipRestriction,app.id)}
                      restrictedTooltipMsg={getTooltipMsg(ipRestriction,app.id)?.message || restrictedTooltipMsg}
                      openInNewTab={app.openInNewTab}
                      showNewIndicator={app.showNewIndicator as boolean}
                      isDefault={isAppDefault(app.id)}
                      warningMessage={app.warningMessage}
                      requiredOS={app.requiredOS}
                    />
                    )));
            })}
          </Styles.CardList>
            ) : (
               <div>
                      <div>
                        {t('applicationList.noApplnAssigned')}
                      </div>
                      <div>
                        {t('applicationList.noApplnAssignedDesc')}
                      </div>
                    </div>
            )}
        </>
    );
}
